import type { GameConfig } from '../types.js';

export function audioSources(config: GameConfig): string[] {
    const audio = config.audio;
    if (!audio) return [];

    return [...new Set([
        audio.background,
        audio.music?.title,
        ...Object.values(audio.music?.stages ?? {}),
        audio.sfx?.click,
        audio.sfx?.questOpen,
        audio.sfx?.questAnswer,
        audio.sfx?.victory,
        audio.sfx?.gameOver,
    ].filter((source): source is string => Boolean(source)))];
}

/** Loads game images and shared HUD art, tolerating missing assets. */
export class AssetLoader {
    readonly assetsPath: string;
    private readonly images: Record<string, HTMLImageElement> = {};
    private totalAssets = 0;
    private loadedAssets = 0;

    constructor(assetsPath?: string) {
        this.assetsPath = assetsPath ?? '';
    }

    resolve(src: string): string {
        return /^https?:\/\//i.test(src) ? src : this.assetsPath + src;
    }

    private done(): void {
        this.loadedAssets++;
        this.updateProgress();
    }

    async loadImage(src: string): Promise<HTMLImageElement | null> {
        return this.loadImageUrl(this.resolve(src));
    }

    async loadImageUrl(url: string): Promise<HTMLImageElement | null> {
        const cached = this.images[url];
        if (cached) return cached;

        return new Promise((resolve) => {
            const image = new Image();
            image.addEventListener('load', () => {
                this.images[url] = image;
                this.done();
                resolve(image);
            }, { once: true });
            image.addEventListener('error', () => {
                console.warn(`Failed to load image: ${url}`);
                this.done();
                resolve(null);
            }, { once: true });
            image.src = url;
        });
    }

    async loadAll(config: GameConfig, extraImageUrls: string[] = []): Promise<void> {
        const images = new Set<string>();
        const rawImages = new Set<string>(extraImageUrls);

        if (config.world?.ground?.sprite) images.add(config.world.ground.sprite);
        if (config.world?.background) images.add(config.world.background);
        if (config.world?.foreground) images.add(config.world.foreground);

        (config.objects ?? []).forEach((object) => {
            if (object.sprite) images.add(object.sprite);
        });

        (config.categories ?? []).forEach((category) => {
            if (category.icon) images.add(category.icon);
        });

        (config.resources ?? []).forEach((resource) => {
            if (resource.source) images.add(resource.source);
            if (resource.unit) images.add(resource.unit);
        });

        (config.quests ?? []).forEach((quest) => {
            if (quest.speaker?.portrait) images.add(quest.speaker.portrait);
        });

        [...images].forEach((src) => rawImages.delete(this.resolve(src)));

        this.totalAssets = images.size + rawImages.size;
        this.updateProgress();

        await Promise.all([
            ...[...images].map((src) => this.loadImage(src)),
            ...[...rawImages].map((url) => this.loadImageUrl(url)),
        ]);
    }

    updateProgress(): void {
        const progress = this.totalAssets > 0
            ? Math.round((this.loadedAssets / this.totalAssets) * 100)
            : 100;

        const loadingBar = document.getElementById('loadingBar');
        const loadingText = document.getElementById('loadingText');
        const startButton = document.getElementById('startButton');

        if (loadingBar) loadingBar.style.width = `${progress}%`;
        if (loadingText) loadingText.textContent = `${progress}%`;

        if (progress === 100 && startButton) startButton.classList.add('ready');
    }

    getImage(src: string | null | undefined): HTMLImageElement | null {
        return src ? this.images[this.resolve(src)] ?? null : null;
    }

    getImageUrl(url: string | null | undefined): HTMLImageElement | null {
        return url ? this.images[url] ?? null : null;
    }
}
